import { useState } from "react";
import { IN_DISCORD } from "../api";
import { openExternal } from "../bootstrap";
import { LOCALE_NAMES, getLocale, setLocale, t, type Locale } from "../i18n";
import type { Pin } from "@tarkov-callout/shared";
import type { AppSession } from "../bootstrap";
import {
  EXTRACT_FACTIONS,
  MAPS,
  drawsLayer,
  extractCounts,
  hasAnyExtracts,
  hasExtracts,
  hasSpawns,
  mapLicense,
  mapTitle,
  type ExtractFaction,
  type TarkovMap,
} from "../maps";
import type { MapStyle } from "../map/MapCanvas";
import type { RoomController } from "../useRoom";
import { FeedbackDialog } from "./FeedbackDialog";
import { HandoffDialog } from "./HandoffDialog";
import { InviteDialog } from "./InviteDialog";
import { PinList } from "./PinList";
import { used } from "../usage";

interface Props {
  room: RoomController;
  map: TarkovMap;
  style: MapStyle;
  onStyleChange: (style: MapStyle) => void;
  activeLayer: string | null;
  onLayerChange: (layer: string | null) => void;
  showExtracts: boolean;
  onShowExtractsChange: (show: boolean) => void;
  showSpawns: boolean;
  onShowSpawnsChange: (show: boolean) => void;
  factions: ExtractFaction[];
  onFactionsChange: (factions: ExtractFaction[]) => void;
  session: AppSession;
  selectedId: string | null;
  onSelectPin: (pin: Pin) => void;
  onLabelPin: (id: string, label: string) => void;
  onRemovePin: (id: string) => void;
}

export function Sidebar({
  room,
  map,
  style,
  onStyleChange,
  activeLayer,
  onLayerChange,
  showExtracts,
  onShowExtractsChange,
  showSpawns,
  onShowSpawnsChange,
  factions,
  onFactionsChange,
  session,
  selectedId,
  onSelectPin,
  onLabelPin,
  onRemovePin,
}: Props) {
  const bothStyles = Boolean(map.svgPath) && Boolean(map.tilePath);
  const otherStyle: MapStyle = style === "svg" ? "tile" : "svg";
  /*
   * A floor the current rendering cannot draw is still offered, because the
   * other rendering can draw it and switching is one click we can make for
   * them. It is marked, though: the base artwork changes underneath, and a
   * button that silently redraws the whole map is its own kind of broken.
   */
  const floors = map.layers.map((layer) => ({
    layer,
    needsSwitch: !drawsLayer(map, layer, style) && drawsLayer(map, layer, otherStyle),
  }));
  const anySwitches = floors.some((floor) => floor.needsSwitch);
  // CC BY asks for the licence alongside the credit, not just the author.
  const license = mapLicense(map);
  const counts = extractCounts(map.id);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  /** Says the room link went to the clipboard, which is otherwise invisible. */
  const [copied, setCopied] = useState(false);
  /*
   * Shut to begin with. The map is the point of the window, and a list that
   * opens itself pushes the roster and the buttons below the fold on a short
   * one. It is a place to go looking, not something to be shown.
   */
  const [listOpen, setListOpen] = useState(false);
  /** Only what is on the map in front of them; the count has to agree with the list. */
  const onThisMap = room.pins.filter((pin) => pin.mapId === map.id);
  const [, forceRedraw] = useState(0);

  return (
    <aside className="sidebar">
      <div className="sidebar__brand">
        <strong>Tarkov ShareMap</strong>
        <span className={`status status--${room.status}`}>
          {room.status === "open"
            ? t("status.syncing")
            : room.status === "connecting"
              ? t("status.connecting")
              : room.status === "full"
                ? t("status.full")
                : t("status.closed")}
        </span>
      </div>

      {/*
        Directly under the brand rather than in the footer: the roster panel
        grows, so anything below it drops off the bottom of a short activity
        window, and a language switch you have to scroll to find is one nobody
        finds. Affects only your own view, like the floor and style toggles.
      */}
      <select
        className="select select--compact sidebar__lang"
        value={getLocale()}
        aria-label="Language"
        onChange={(event) => {
          setLocale(event.target.value as Locale);
          forceRedraw((n) => n + 1);
        }}
      >
        {Object.entries(LOCALE_NAMES).map(([code, name]) => (
          <option key={code} value={code}>
            {name}
          </option>
        ))}
      </select>

      <section className="panel">
        <label className="panel__title" htmlFor="map-select">
          {t("sidebar.map")}
        </label>
        <select
          id="map-select"
          className="select"
          value={map.id}
          onChange={(event) => room.setMap(event.target.value)}
        >
          {MAPS.map((candidate) => (
            <option key={candidate.id} value={candidate.id}>
              {mapTitle(candidate)}
            </option>
          ))}
        </select>

        {hasAnyExtracts() && (
          <>
            <label className="toggle">
              <input
                type="checkbox"
                checked={showExtracts}
                disabled={!hasExtracts(map.id)}
                onChange={(event) => {
                  used("extracts");
                  onShowExtractsChange(event.target.checked);
                }}
              />
              <span>{t("extracts.show")}</span>
            </label>
            {showExtracts && hasExtracts(map.id) && (
              <div className="extract-filter" role="group" aria-label={t("extracts.show")}>
                {EXTRACT_FACTIONS.filter((faction) => counts[faction] > 0).map((faction) => {
                  const on = factions.includes(faction);
                  return (
                    <button
                      key={faction}
                      type="button"
                      className={`extract-filter__item extract-filter__item--${faction}${on ? "" : " is-off"}`}
                      aria-pressed={on}
                      onClick={() => {
                        used("extractFilter");
                        onFactionsChange(
                          on
                            ? factions.filter((one) => one !== faction)
                            : EXTRACT_FACTIONS.filter((one) => one === faction || factions.includes(one)),
                        );
                      }}
                    >
                      <i aria-hidden="true" />
                      {t(`extracts.${faction}`)}
                      <b>{counts[faction]}</b>
                    </button>
                  );
                })}
              </div>
            )}
            {!hasExtracts(map.id) && <p className="panel__note">{t("extracts.none")}</p>}
          </>
        )}

        {/*
          Separate from the extracts, and off by default for the same reason:
          it is a hint about where people come in, not part of the map.
        */}
        {hasSpawns(map.id) && (
          <label className="toggle">
            <input
              type="checkbox"
              checked={showSpawns}
              onChange={(event) => {
                used("spawns");
                onShowSpawnsChange(event.target.checked);
              }}
            />
            <span>{t("spawns.show")}</span>
          </label>
        )}
        {showSpawns && hasSpawns(map.id) && <p className="panel__note">{t("spawns.note")}</p>}

        {bothStyles && (
          <div className="segmented">
            <button
              type="button"
              className={style === "svg" ? "is-active" : ""}
              onClick={() => {
                used("style");
                onStyleChange("svg");
              }}
            >
              {t("sidebar.styleSvg")}
            </button>
            <button
              type="button"
              className={style === "tile" ? "is-active" : ""}
              onClick={() => {
                used("style");
                onStyleChange("tile");
              }}
            >
              {t("sidebar.styleTile")}
            </button>
          </div>
        )}
      </section>

      {floors.length > 0 && (
        <section className="panel">
          <span className="panel__title">{t("sidebar.floors")}</span>
          <div className="layers">
            <button
              type="button"
              className={activeLayer === null ? "is-active" : ""}
              onClick={() => {
                used("floor");
                onLayerChange(null);
              }}
            >
              {t("sidebar.groundFloor")}
            </button>
            {floors.map(({ layer, needsSwitch }) => (
              <button
                key={layer.name}
                type="button"
                className={activeLayer === layer.name ? "is-active" : ""}
                onClick={() => {
                  used("floor");
                  if (needsSwitch) onStyleChange(otherStyle);
                  onLayerChange(layer.name);
                }}
              >
                {layer.name}
                {needsSwitch && <span className="layers__switches">*</span>}
              </button>
            ))}
          </div>
          {anySwitches && (
            <p className="panel__note">
              {t("sidebar.floorOtherStyle", {
                style: t(otherStyle === "svg" ? "sidebar.styleSvg" : "sidebar.styleTile"),
              })}
            </p>
          )}
          <p className="panel__note">{t("sidebar.floorNote")}</p>
        </section>
      )}

      <section className="panel">
        <button
          type="button"
          className="panel__disclosure"
          aria-expanded={listOpen}
          onClick={() => {
            used("list");
            setListOpen((open) => !open);
          }}
        >
          <span className="panel__title">{t("list.title")}</span>
          <span className="panel__count">{onThisMap.length}</span>
          <span className="panel__caret" aria-hidden="true">
            {listOpen ? "▾" : "▸"}
          </span>
        </button>
        {listOpen && (
          <PinList
            pins={room.pins}
            mapId={map.id}
            activeLayer={activeLayer}
            selfId={room.selfId}
            selectedId={selectedId}
            onSelect={onSelectPin}
            onLabel={onLabelPin}
            onRemove={onRemovePin}
          />
        )}
      </section>

      <section className="panel panel--grow">
        <span className="panel__title">{t("sidebar.members", { count: room.members.length })}</span>
        <ul className="roster">
          {room.members.map((member) => (
            <li key={member.id} className="roster__row">
              <span className="roster__chip" style={{ background: member.color }} />
              {member.avatarUrl && <img className="roster__avatar" src={member.avatarUrl} alt="" />}
              <span className="roster__name">{member.name}</span>
              {member.guest && <span className="roster__guest">{t("sidebar.guest")}</span>}
              {member.id === room.selfId && <span className="roster__you">{t("sidebar.you")}</span>}
            </li>
          ))}
        </ul>
      </section>

      {/*
        A map started in a browser has no voice call to decide who is in it, so
        the address is the way in. It is shown rather than hidden behind the
        invite dialog because it is the answer to the first question someone
        has here: "how do I get my friend in?"
      */}
      {session.roomUrl && (
        <section className="panel">
          <span className="panel__title">{t("sidebar.roomLink")}</span>
          <input className="room-link" readOnly value={session.roomUrl} onFocus={(event) => event.currentTarget.select()} />
          <button
            type="button"
            className="sidebar__feedback"
            onClick={() => {
              used("roomLink");
              void navigator.clipboard?.writeText(session.roomUrl ?? "").catch(() => {
                // Nothing to say: the field beside it is already selectable.
              });
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1500);
            }}
          >
            {copied ? t("sidebar.roomLinkCopied") : t("sidebar.roomLinkCopy")}
          </button>
          <p className="panel__note">{t("sidebar.roomLinkNote")}</p>
        </section>
      )}

      <section className="panel panel--footer">

        <button type="button" className="danger" onClick={() => room.clear("all")}>
          {t("sidebar.clearAll")}
        </button>
        {session.inDiscord && (
          <button type="button" className="sidebar__feedback" onClick={() => {
              used("handoff");
              setHandoffOpen(true);
            }}>
            {t("sidebar.openWindow")}
          </button>
        )}
        <button type="button" className="sidebar__feedback" onClick={() => {
            used("invite");
            setInviteOpen(true);
          }}>
          {t("sidebar.invite")}
        </button>
        {session.publicUrl && (
          <button
            type="button"
            className="sidebar__feedback"
            onClick={() => {
              used("help");
              void openExternal(`${session.publicUrl}/help`);
            }}
          >
            {t("sidebar.help")}
          </button>
        )}
        {session.feedbackEnabled && (
          <button type="button" className="sidebar__feedback" onClick={() => {
            used("feedback");
            setFeedbackOpen(true);
          }}>
            {t("sidebar.feedback")}
          </button>
        )}
        <p className="credits">
          {t("sidebar.mapData")}: {map.author ?? "tarkov.dev"}
          {map.authorLink &&
            (IN_DISCORD ? (
              <span className="credits__url"> — {new URL(map.authorLink).hostname}</span>
            ) : (
              <>
                {" — "}
                <a href={map.authorLink} target="_blank" rel="noreferrer">
                  {new URL(map.authorLink).hostname}
                </a>
              </>
            ))}
        </p>
        {license && (
          <p className="credits">
            {t("sidebar.license")}:{" "}
            {IN_DISCORD ? (
              license.name
            ) : (
              <a href={license.url} target="_blank" rel="noreferrer">
                {license.name}
              </a>
            )}
            {" "}— {t("sidebar.licenseNote")}
          </p>
        )}
        {!session.inDiscord && <p className="credits">{t("sidebar.room")}: {session.instanceId}</p>}

        {/*
          Only when Discord refused interactive picture-in-picture. Silence on
          success; on failure the window looks broken for no visible reason,
          and the message is the only way anyone can tell us why.
        */}
        {session.trace && (
          <p className="credits">
            {t("trace.label")}: <span className="credits__trace">{session.trace}</span>
          </p>
        )}
        {session.pip && !session.pip.interactive && (
          <p className="credits credits--warn">
            PiP: {session.pip.error ?? "not interactive"} [{session.pip.platform}]
          </p>
        )}
      </section>

      {handoffOpen && (
        <HandoffDialog
          session={session.session}
          instanceId={session.instanceId}
          onClose={() => setHandoffOpen(false)}
        />
      )}

      {inviteOpen && (
        <InviteDialog
          session={session.session}
          instanceId={session.instanceId}
          onClose={() => setInviteOpen(false)}
        />
      )}

      {feedbackOpen && (
        <FeedbackDialog
          session={session.session}
          context={`${mapTitle(map)} / ${style === "svg" ? t("sidebar.styleSvg") : t("sidebar.styleTile")}`}
          room={session.instanceId}
          platform={session.pip?.platform ?? (session.inDiscord ? "discord" : "browser")}
          onClose={() => setFeedbackOpen(false)}
        />
      )}
    </aside>
  );
}
